import { Link } from "react-router-dom";
import {
  ArrowRight,
  GraduationCap,
  ClipboardList,
  ShieldCheck,
  AlertTriangle,
  Briefcase,
  Receipt,
  HeartHandshake,
  Compass,
  Monitor,
  Sparkles,
  Users,
  CheckCircle2,
} from "lucide-react";
import SEO from "@/components/SEO";

const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";

const HERO_IMG = "/team/sophia-supervision.png";

const offerings = [
  { icon: ClipboardList, label: "Case consultation and clinical decision-making" },
  { icon: ShieldCheck, label: "Ethics, boundaries, and professional use of self" },
  { icon: Briefcase, label: "Documentation, treatment planning, and diagnosis" },
  { icon: AlertTriangle, label: "Risk assessment and crisis response" },
  { icon: GraduationCap, label: "Private practice preparation" },
  { icon: Receipt, label: "Insurance, billing, and clinical workflow exposure" },
  { icon: HeartHandshake, label: "Client engagement and therapeutic presence" },
  { icon: Compass, label: "Confidence, identity, and leadership as a clinician" },
];

const styleBullets = [
  "Reflective",
  "Ethical",
  "Practical",
  "Honest",
  "Culturally aware",
  "Client-centered",
  "Focused on professional growth",
];

const whoFor = [
  "Wants to grow into clinical confidence",
  "Needs structure and accountability",
  "Is interested in outpatient or private practice work",
  "Wants support with documentation and diagnosis",
  "Values ethical and culturally responsive practice",
  "Wants supervision that connects real cases to real clinical growth",
];

export default function LcswSupervision() {
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "LCSW Supervision in Texas",
    serviceType: "Clinical supervision for LMSWs working toward LCSW licensure",
    provider: {
      "@type": "ProfessionalService",
      name: "Seven Pillars of Life Therapy",
      url: "https://www.7pillarsoflife.com",
      areaServed: { "@type": "State", name: "Texas" },
      founder: { "@type": "Person", name: "Sophia Dinwiddie-Donald, MSSW, LCSW-S" },
    },
    areaServed: { "@type": "State", name: "Texas" },
    audience: { "@type": "Audience", audienceType: "LMSW seeking LCSW licensure" },
    description:
      "Online LCSW supervision in Texas for LMSWs seeking supportive, structured clinical supervision, case consultation, documentation support, and private practice growth.",
  };

  return (
    <div data-testid="lcsw-supervision-page">
      <SEO
        title="LCSW Supervision in Texas"
        description="Online LCSW supervision in Texas for LMSWs seeking supportive, structured clinical supervision, case consultation, documentation support, and private practice growth."
        path="/lcsw-supervision"
        image={HERO_IMG}
        jsonLd={serviceJsonLd}
      />

      {/* HERO — Sophia photo as soft background overlay, focus on title/CTA */}
      <section className="relative bg-forest text-bone overflow-hidden" data-testid="lcsw-hero">
        {/* Soft photo overlay — right side, very low opacity, blurred edges */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute right-0 top-0 h-full w-full md:w-[58%] bg-no-repeat bg-right-top opacity-20 md:opacity-25"
            style={{
              backgroundImage: `url(${HERO_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "right top",
              maskImage: "linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 95%)",
              WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 95%)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/85 to-forest/40" />
        </div>
        {/* dot grid pattern, very subtle */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: "radial-gradient(#FAF7FC 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="max-w-3xl animate-fade-up">
            <p className="overline text-terracotta mb-5 inline-flex items-center gap-2">
              <GraduationCap size={12} strokeWidth={1.6} /> For LMSWs in Texas
            </p>
            <h1 className="font-display text-bone font-semibold text-5xl md:text-7xl leading-[1.04] tracking-tight" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}>
              LCSW Supervision <em className="italic text-terracotta">in Texas.</em>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-bone/90 max-w-2xl leading-relaxed">
              Supportive, structured supervision for LMSWs growing into confident, ethical, clinically grounded LCSWs.
            </p>
            <p className="mt-4 text-bone/70 text-sm italic">
              With Sophia Dinwiddie-Donald, MSSW, LCSW-S — founder of Seven Pillars of Life.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lcsw-hero-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-8 py-4 font-medium transition-colors"
              >
                Request Supervision Consultation <ArrowRight size={18} strokeWidth={1.6} />
              </a>
              <Link
                to="/contact"
                data-testid="lcsw-hero-contact-btn"
                className="inline-flex items-center justify-center bg-transparent border border-bone/60 text-bone hover:bg-bone hover:text-forest rounded-full px-8 py-4 font-medium transition-colors"
              >
                Send a message
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 bg-bone" data-testid="lcsw-intro-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline mb-5">More Than Checking Boxes</p>
          <h2 className="font-display text-3xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
            Supervision built around <em className="italic text-terracotta">clinical voice, ethics, and growth.</em>
          </h2>
          <div className="mt-8 space-y-5 text-lg text-ink/80 leading-relaxed">
            <p>
              At Seven Pillars of Life, LCSW supervision is more than checking boxes for licensure. It is a space to strengthen your clinical voice, sharpen your documentation, build confidence with complex cases, and grow into the kind of social worker who can practice with clarity, ethics, and purpose.
            </p>
            <p>
              Supervision is offered by <strong className="text-forest">Sophia Dinwiddie-Donald, MSSW, LCSW-S</strong>, founder of Seven Pillars of Life, LLC.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT I OFFER */}
      <section className="py-20 md:py-28 bg-surface" data-testid="lcsw-offer-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">What I Offer</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Supervision that is <em className="italic text-terracotta">practical, supportive, and professionally challenging.</em>
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              I provide supervision for LMSWs in Texas who are working toward clinical licensure and want a supervision experience that grows them as people and as clinicians.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {offerings.map((o) => {
              const Icon = o.icon;
              return (
                <article
                  key={o.label}
                  data-testid={`lcsw-offer-${o.label.toLowerCase().slice(0, 25).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="bg-bone border border-sage/25 rounded-2xl p-7 hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-11 w-11 rounded-full bg-forest/5 flex items-center justify-center text-forest mb-5">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <p className="text-ink/85 leading-snug font-medium text-[15px]">{o.label}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* TWO PATHS */}
      <section className="py-20 md:py-28 bg-bone" data-testid="lcsw-paths-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">Choose Your Path</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              The supervision path <em className="italic text-terracotta">that fits you.</em>
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              LMSWs may choose the option that best fits their current clinical goals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Option 1 */}
            <article
              data-testid="lcsw-option-supervision-only"
              className="bg-surface border border-sage/25 rounded-2xl p-9 md:p-11 hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-xl transition-all duration-300 flex flex-col"
            >
              <p className="font-display text-terracotta font-semibold text-5xl leading-none mb-6">01</p>
              <p className="overline mb-3">Option 1</p>
              <h3 className="font-display text-3xl md:text-4xl text-forest font-medium leading-tight mb-5">
                Supervision Only
              </h3>
              <p className="text-ink/75 leading-relaxed mb-4">
                For LMSWs who already have an approved clinical work setting and need board-approved supervision while they continue working with clients through their current employer or agency.
              </p>
              <p className="text-ink/75 leading-relaxed">
                This path includes structured supervision, clinical consultation, ethical guidance, and support with licensure readiness.
              </p>
            </article>

            {/* Option 2 — spotlight */}
            <article
              data-testid="lcsw-option-through-7pillars"
              className="bg-forest text-bone rounded-2xl p-9 md:p-11 hover:bg-[#5D4B85] transition-colors duration-300 flex flex-col"
            >
              <p className="font-display text-terracotta font-semibold text-5xl leading-none mb-6">02</p>
              <p className="overline text-terracotta mb-3">Option 2</p>
              <h3 className="font-display text-3xl md:text-4xl text-bone font-medium leading-tight mb-5">
                Supervision Through Seven Pillars
              </h3>
              <p className="text-bone/85 leading-relaxed mb-4">
                For LMSWs who are a good fit, there may be an opportunity to see clients through Seven Pillars of Life while receiving supervision. This option allows supervisees to gain private practice experience, strengthen clinical skills, and learn the business and documentation flow of outpatient therapy.
              </p>
              <p className="text-bone/80 leading-relaxed italic text-sm">
                Based on fit, availability, professionalism, documentation readiness, and practice needs.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ONLINE */}
      <section className="py-20 md:py-28 bg-surface" data-testid="lcsw-online-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-full h-full bg-sage/40 rounded-2xl rotate-[-2deg]" />
              <div className="relative bg-forest text-bone rounded-2xl p-10 md:p-12">
                <Monitor size={32} className="text-terracotta mb-6" strokeWidth={1.4} />
                <p className="font-display text-3xl md:text-4xl font-medium leading-tight">
                  Available <em className="italic text-terracotta">online</em> for LMSWs across Texas.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline mb-5">Online Supervision Available</p>
            <h2 className="font-display text-3xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Flexible structure, <em className="italic text-terracotta">real accountability.</em>
            </h2>
            <p className="mt-6 text-lg text-ink/75 leading-relaxed">
              Supervision is available online for eligible LMSWs in Texas. Online supervision allows flexibility while still providing structure, accountability, and meaningful clinical support.
            </p>
            <p className="mt-4 text-ink/75 leading-relaxed">
              In-person supervision may also be available when appropriate.
            </p>
          </div>
        </div>
      </section>

      {/* STYLE */}
      <section className="py-24 md:py-32 bg-forest text-bone relative overflow-hidden" data-testid="lcsw-style-section">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: "radial-gradient(#FAF7FC 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <p className="overline text-terracotta mb-5">My Supervision Style</p>
            <h2 className="font-display text-4xl md:text-6xl font-medium leading-[1.05] tracking-tight">
              Direct. Supportive. <em className="italic text-terracotta">Growth-oriented.</em>
            </h2>
            <p className="mt-6 text-bone/80 leading-relaxed text-lg">
              My style helps supervisees think deeper, prepare better, and show up with more confidence in the therapy room.
            </p>
            <p className="mt-4 text-bone/85 leading-relaxed italic font-display text-xl">
              The goal is not only to help you complete your hours — it is to help you become a stronger clinician.
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="text-bone/70 mb-6 text-sm tracking-wide">You can expect a supervision space that is:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {styleBullets.map((s, i) => (
                <li key={s} className="flex items-center gap-4 border-b border-bone/15 pb-4">
                  <span className="font-display text-terracotta text-2xl font-semibold w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-bone font-display text-2xl">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 md:py-32 bg-bone" data-testid="lcsw-whofor-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline mb-5">Who This Is For</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Built for LMSWs who <em className="italic text-terracotta">want more.</em>
            </h2>
            <p className="mt-6 text-ink/70 leading-relaxed">
              This supervision may be a good fit if you're an LMSW who:
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {whoFor.map((w, i) => (
              <li
                key={i}
                className="flex gap-5 items-start p-6 bg-surface rounded-2xl border border-sage/25 hover:border-terracotta/40 transition-all duration-300"
              >
                <CheckCircle2 size={22} strokeWidth={1.5} className="text-terracotta mt-0.5 shrink-0" />
                <p className="text-lg text-ink/85 leading-relaxed">{w}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONSULTATION REQUIRED + CTA */}
      <section className="py-24 md:py-32 bg-surface" data-testid="lcsw-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Users size={28} className="text-terracotta mx-auto mb-6" strokeWidth={1.4} />
          <p className="overline mb-5">Consultation Required</p>
          <h2 className="font-display text-4xl md:text-6xl text-forest font-medium leading-tight tracking-tight">
            Begin with a <em className="italic text-terracotta">conversation.</em>
          </h2>
          <p className="mt-6 text-lg text-ink/75 max-w-2xl mx-auto leading-relaxed">
            Supervision begins with a consultation to determine fit, goals, availability, and whether supervision only or supervised client work through Seven Pillars is the best path.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lcsw-final-book-btn"
              className="inline-flex items-center justify-center gap-2 bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-8 py-4 font-medium transition-colors"
            >
              Request Supervision Consultation <ArrowRight size={16} />
            </a>
            <Link
              to="/contact"
              data-testid="lcsw-final-contact-btn"
              className="inline-flex items-center justify-center bg-transparent border border-forest text-forest hover:bg-forest hover:text-bone rounded-full px-8 py-4 font-medium transition-colors"
            >
              Ask a question first
            </Link>
          </div>
          <p className="mt-8 text-sm text-ink/55 italic">
            Sophia Dinwiddie-Donald, MSSW, LCSW-S — founder of Seven Pillars of Life, LLC.
          </p>
        </div>
      </section>
    </div>
  );
}
